import { COUNTRY_OPTIONS, countryName } from "@lib/countryCodes";
import { X } from "lucide-react";
import { useRef, useState } from "react";

interface CountryCodeSelectProps {
  value: string[];
  onChange: (codes: string[]) => void;
  label?: string;
  hint?: string;
}

export function CountryCodeSelect({ value, onChange, label, hint }: CountryCodeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const available = COUNTRY_OPTIONS.filter(
    (c) => !value.includes(c.code) && c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleAdd = (code: string) => {
    onChange([...value, code]);
    setSearch("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleRemove = (code: string) => {
    onChange(value.filter((c) => c !== code));
  };

  return (
    <div className="space-y-1">
      {label && <label className="text-xs font-medium text-text-secondary">{label}</label>}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}

      <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-white px-2 py-1.5 dark:bg-zinc-900">
        {value.map((code) => (
          <span
            key={code}
            className="inline-flex items-center gap-1 rounded bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent"
          >
            {countryName(code)}
            <button
              type="button"
              onClick={() => handleRemove(code)}
              className="rounded-full p-0.5 hover:bg-accent/20"
            >
              <X size={10} />
            </button>
          </span>
        ))}

        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={value.length === 0 ? "Type to search countries..." : "Add..."}
            className="w-full min-w-[80px] border-none bg-transparent py-0.5 text-xs text-text-primary outline-none placeholder:text-text-faint"
          />

          {open && available.length > 0 && (
            <div className="absolute left-0 top-full z-50 mt-1 max-h-48 w-56 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
              {available.slice(0, 15).map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleAdd(c.code)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-accent/5"
                >
                  <span className="font-mono text-text-faint">{c.code.toUpperCase()}</span>
                  <span className="text-text-primary">{c.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
